import {
  Point as CameraPoint,
  PresenceZone,
  PresenceZoneKind,
  PresenceZoneRequest,
} from '@hakimo-ui/hakimo/types';
import {
  cameraZoneToCanvasZone,
  canvasZoneToCameraPoints,
  CanvasDimensions,
  ImageResolution,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';

function canvasTypeFor(kind: PresenceZoneKind | undefined): SpecialZoneType {
  return kind === 'line'
    ? SpecialZoneType.PresenceLine
    : SpecialZoneType.PresenceZone;
}

export function kindFor(zone: ZonePolygonData): PresenceZoneKind {
  return zone.type === SpecialZoneType.PresenceLine ? 'line' : 'area';
}

/**
 * Server zones as canvas polygons -- or polylines, for lines.
 *
 * A zone's polygon lives in its own labellingResolution space, which is the
 * image size when it was drawn here — but a zone seeded by the backend tool
 * may carry none, in which case the backend treats it as frame coordinates
 * and so do we, taking the current image as the frame. The whole-camera
 * zone has no polygon and is not drawn.
 */
export function presenceZonesToCanvas(
  zones: PresenceZone[],
  imageResolution: ImageResolution,
  canvasDimensions: CanvasDimensions
): ZonePolygonData[] {
  return zones
    .filter((zone) => !zone.isDefault && zone.polygon)
    .map((zone) => {
      const resolution: ImageResolution = zone.labellingResolution
        ? {
            width: zone.labellingResolution[0],
            height: zone.labellingResolution[1],
          }
        : imageResolution;
      return {
        ...cameraZoneToCanvasZone(
          zone.polygon as CameraPoint[],
          zone.id,
          canvasTypeFor(zone.kind),
          resolution,
          canvasDimensions
        ),
        label: zone.name,
      };
    });
}

/**
 * A canvas polygon as the request that creates or redraws it.
 *
 * `kind` rides only on a create: the API refuses it on an update, since a
 * line turned into an area would strand the crossings and rules written
 * against it.
 */
export function canvasZoneToRequest(
  zone: ZonePolygonData,
  name: string,
  canvasDimensions: CanvasDimensions,
  imageResolution: ImageResolution,
  options: { isNew?: boolean } = {}
): PresenceZoneRequest {
  const polygon = canvasZoneToCameraPoints(
    zone,
    canvasDimensions,
    imageResolution
  ).map(([x, y]) => [Math.round(x), Math.round(y)] as CameraPoint);
  return {
    name: name.trim(),
    ...(options.isNew ? { kind: kindFor(zone) } : {}),
    polygon,
    labellingResolution: [imageResolution.width, imageResolution.height],
  };
}

export function samePoints(a: ZonePolygonData, b: ZonePolygonData): boolean {
  if (a.points.length !== b.points.length) return false;
  return a.points.every(
    (p, i) =>
      Math.abs(p.x - b.points[i].x) < 0.5 && Math.abs(p.y - b.points[i].y) < 0.5
  );
}

/**
 * Whether a line now runs the other way round from before: its end-to-end
 * vector points back against the old one. A flip does this; so does a drag
 * that swaps its ends.
 */
export function reversesLine(before: ZonePolygonData, after: ZonePolygonData) {
  const run = ({ points }: ZonePolygonData) => ({
    x: points[points.length - 1].x - points[0].x,
    y: points[points.length - 1].y - points[0].y,
  });
  const a = run(before);
  const b = run(after);
  return a.x * b.x + a.y * b.y < 0;
}
